import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import cn from 'classnames';
import { BiContainer, Box, Button, Column, FullPageModal, Row, Tabs, TextButton, TextField, Typography, useOpenUnsavedChangesModal } from 'seagull';
import { Undo } from 'seagull/icons/arrows';
import { useInsertVariableIntoTextField } from '~/components/AgreementStudioPage/TemplateVariables/hooks/useInsertVariableIntoTextField';
import { VariableFieldAdornment } from '~/components/AgreementStudioPage/TemplateVariables/VariableFieldAdornment/VariableFieldAdornment';
import { VariablePickerPopover } from '~/components/AgreementStudioPage/TemplateVariables/VariablePickerPopover/VariablePickerPopover';
import { useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { NotificationSettingsEntryType } from '~/models/NotificationSettingsModel';
import { selectors } from '~/store/state-selectors';
import { useRevertNotificationSetting } from '../../hooks/useRevertNotificationSetting';
import { useUpdateNotificationSettingsV2 } from '../../hooks/useUpdateNotificationSettingsV2';
import { deriveIsActive, Tab } from '../../NotificationSettings.utils';
import { NotificationEmailPreview } from '../NotificationEmailPreview/NotificationEmailPreview';
import { NotifyRow } from './components/NotifyRow';
import styles from './NotificationEditModal.module.scss';
import { GetNotificationEditModalTID } from './NotificationEditModal.testIds';
import { isDraftValid, validateDraft, SUBJECT_MAX_LENGTH } from './NotificationEditModal.utils';
const resolveErrorText = (errors, code)=>{
    if (code == null) return undefined;
    switch(code){
        case 'NOTIFICATION_V2_SUBJECT_EMPTY':
            return errors.NOTIFICATION_V2_SUBJECT_EMPTY();
        case 'NOTIFICATION_V2_SUBJECT_NEWLINE':
            return errors.NOTIFICATION_V2_SUBJECT_NEWLINE();
        case 'NOTIFICATION_V2_SUBJECT_TOO_LONG':
            return errors.NOTIFICATION_V2_SUBJECT_TOO_LONG({
                limit: SUBJECT_MAX_LENGTH
            });
        case 'NOTIFICATION_V2_MESSAGE_EMPTY':
            return errors.NOTIFICATION_V2_MESSAGE_EMPTY();
        case 'NOTIFICATION_V2_CONTENT_NOT_EDITABLE':
            return errors.NOTIFICATION_V2_CONTENT_NOT_EDITABLE();
        case 'NOTIFICATION_V2_RECIPIENTS_NOT_EDITABLE':
            return errors.NOTIFICATION_V2_RECIPIENTS_NOT_EDITABLE();
        case 'NOTIFICATION_V2_MISSING_REQUIRED_TAG':
            return errors.NOTIFICATION_V2_MISSING_REQUIRED_TAG();
        case 'NOTIFICATION_V2_UNKNOWN_TAG':
            return errors.NOTIFICATION_V2_UNKNOWN_TAG();
        default:
            return errors.default();
    }
};
const EMPTY_VARIANT = {
    subject: '',
    message: ''
};
const EMPTY_VARIANTS = {};
const VARIANT_LABEL_OVERRIDES = {
    invoiceIssuedAutoProcess: {
        standard: (t)=>t.byNotification.invoiceIssuedAutoProcess.standard()
    },
    invoiceIssuedRequiresApproval: {
        auto: (t)=>t.byNotification.invoiceIssuedRequiresApproval.auto(),
        manual: (t)=>t.byNotification.invoiceIssuedRequiresApproval.manual()
    },
    payoutDepositedVendor: {
        standard: (t)=>t.byNotification.payoutDepositedVendor.standard(),
        withdrawal: (t)=>t.byNotification.payoutDepositedVendor.withdrawal()
    },
    payoutFailedVendor: {
        standard: (t)=>t.byNotification.payoutFailedVendor.standard(),
        withdrawal: (t)=>t.byNotification.payoutFailedVendor.withdrawal()
    },
    relationshipUpdatedWithChanges: {
        standard: (t)=>t.byNotification.relationshipUpdatedWithChanges.standard()
    }
};
const VARIANT_LABEL_PATH_PREFIX = 'pages.notificationSettings.contentCustomization.variantLabels.';
const spacedKey = (key)=>{
    const spaced = key.replace(/([A-Z])/g, ' $1').trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};
const resolveVariantLabel = (notificationName, key, variantLabels)=>{
    var _VARIANT_LABEL_OVERRIDES_notificationName;
    const override = (_VARIANT_LABEL_OVERRIDES_notificationName = VARIANT_LABEL_OVERRIDES[notificationName]) === null || _VARIANT_LABEL_OVERRIDES_notificationName === void 0 ? void 0 : _VARIANT_LABEL_OVERRIDES_notificationName[key];
    if (override != null) {
        const overrideValue = override(variantLabels);
        // Leaked path on a declared override → humanize key, never fall back to the generic map (auto/manual exist there with unrelated descriptors).
        return overrideValue.startsWith(VARIANT_LABEL_PATH_PREFIX) ? spacedKey(key) : overrideValue;
    }
    const localized = variantLabels[key];
    if (typeof localized === 'function') {
        const value = localized();
        if (!value.startsWith(VARIANT_LABEL_PATH_PREFIX)) return value;
    }
    return spacedKey(key);
};
const recipientTypesEqual = (a, b)=>{
    if (a.length !== b.length) return false;
    const aSorted = [
        ...a
    ].sort();
    const bSorted = [
        ...b
    ].sort();
    return aSorted.every((value, index)=>value === bSorted[index]);
};
const variantsEqualForKeys = (keys, a, b)=>keys.every((key)=>{
        var _ref, _ref1, _ref2, _ref3;
        var _a_key, _b_key, _a_key1, _b_key1;
        return ((_ref = (_a_key = a[key]) === null || _a_key === void 0 ? void 0 : _a_key.subject) !== null && _ref !== void 0 ? _ref : '') === ((_ref1 = (_b_key = b[key]) === null || _b_key === void 0 ? void 0 : _b_key.subject) !== null && _ref1 !== void 0 ? _ref1 : '') && ((_ref2 = (_a_key1 = a[key]) === null || _a_key1 === void 0 ? void 0 : _a_key1.message) !== null && _ref2 !== void 0 ? _ref2 : '') === ((_ref3 = (_b_key1 = b[key]) === null || _b_key1 === void 0 ? void 0 : _b_key1.message) !== null && _ref3 !== void 0 ? _ref3 : '');
    });
const seedVariants = (source, keys)=>{
    const seeded = {};
    for (const key of keys){
        var _source_key;
        seeded[key] = (_source_key = source[key]) !== null && _source_key !== void 0 ? _source_key : EMPTY_VARIANT;
    }
    return seeded;
};
const deriveVariantKeys = (source)=>{
    const keys = Object.keys(source);
    return keys.length > 0 ? keys : [
        'standard'
    ];
};
export const NotificationEditModal = (param)=>{
    let { notificationName, notification, notificationLabel, selectedTab, customTemplateVariables = [], onClose, onRevertSuccess, testId = 'notification-edit-modal' } = param;
    var _notification_variants, _ref, _ref1, _variants_activeVariantKey, _ref2, _errors_subject, _resolveErrorText, _notification_templateVariables;
    const { NotificationEditModalTestIds: testIds } = useMemo(()=>GetNotificationEditModalTID(testId), [
        testId
    ]);
    const { t: contentTexts } = useLocaleKeys('pages.notificationSettings.contentCustomization');
    const { t: pageTexts } = useLocaleKeys('pages.notificationSettings');
    const businessId = useStore(selectors.user.businessId);
    const businessLogoUrl = useStore(selectors.user.businessLogo);
    const businessName = useStore(selectors.user.businessName);
    const branding = useMemo(()=>selectedTab === Tab.Client ? {
            type: 'business',
            name: businessName !== null && businessName !== void 0 ? businessName : '',
            logoUrl: businessLogoUrl !== null && businessLogoUrl !== void 0 ? businessLogoUrl : undefined
        } : {
            type: 'anchor'
        }, [
        selectedTab,
        businessName,
        businessLogoUrl
    ]);
    const initialVariants = (_notification_variants = notification.variants) !== null && _notification_variants !== void 0 ? _notification_variants : EMPTY_VARIANTS;
    const [variantKeys, setVariantKeys] = useState(()=>deriveVariantKeys(initialVariants));
    const initialVariantKey = variantKeys[0];
    const [variants, setVariants] = useState(()=>seedVariants(initialVariants, variantKeys));
    const [activeVariantKey, setActiveVariantKey] = useState(initialVariantKey);
    const [recipientTypes, setRecipientTypes] = useState(notification.recipientTypes);
    const initialRecurring = notification.type === NotificationSettingsEntryType.Recurring ? notification.recurring : null;
    const initialMinutes = (_ref = initialRecurring === null || initialRecurring === void 0 ? void 0 : initialRecurring.minutes) !== null && _ref !== void 0 ? _ref : null;
    const initialTimes = (_ref1 = initialRecurring === null || initialRecurring === void 0 ? void 0 : initialRecurring.times) !== null && _ref1 !== void 0 ? _ref1 : null;
    const [minutes, setMinutes] = useState(initialMinutes);
    const [times, setTimes] = useState(initialTimes);
    const [hasVendorOverrides, setHasVendorOverrides] = useState(notification.hasVendorOverrides);
    const [isRefreshingFromRevert, setIsRefreshingFromRevert] = useState(false);
    const [baseline, setBaseline] = useState(()=>({
            variants: initialVariants,
            recipientTypes: notification.recipientTypes,
            minutes: initialMinutes,
            times: initialTimes
        }));
    const [errors, setErrors] = useState({});
    const subjectInputRef = useRef(null);
    const messageInputRef = useRef(null);
    const subjectAdornmentRef = useRef(null);
    const messageAdornmentRef = useRef(null);
    const [subjectPickerAnchor, setSubjectPickerAnchor] = useState(null);
    const [messagePickerAnchor, setMessagePickerAnchor] = useState(null);
    const activeVariant = (_variants_activeVariantKey = variants[activeVariantKey]) !== null && _variants_activeVariantKey !== void 0 ? _variants_activeVariantKey : EMPTY_VARIANT;
    const subject = activeVariant.subject;
    const message = activeVariant.message;
    const updateActiveVariant = (patch)=>{
        setVariants((prev)=>{
            var _prev_activeVariantKey;
            return _object_spread_props(_object_spread({}, prev), {
                [activeVariantKey]: _object_spread({}, (_prev_activeVariantKey = prev[activeVariantKey]) !== null && _prev_activeVariantKey !== void 0 ? _prev_activeVariantKey : EMPTY_VARIANT, patch)
            });
        });
    };
    const setSubject = (next)=>updateActiveVariant({
            subject: next
        });
    const setMessage = (next)=>updateActiveVariant({
            message: next
        });
    const insertIntoSubject = useInsertVariableIntoTextField(subjectInputRef, setSubject);
    const insertIntoMessage = useInsertVariableIntoTextField(messageInputRef, setMessage);
    const pickerCustomVariables = useMemo(()=>customTemplateVariables.map((variable)=>variable.label), [
        customTemplateVariables
    ]);
    const pickerAllowedSystemKeys = useMemo(()=>{
        var _notification_templateVariables;
        return new Set(Object.keys((_notification_templateVariables = notification.templateVariables) !== null && _notification_templateVariables !== void 0 ? _notification_templateVariables : {}));
    }, [
        notification.templateVariables
    ]);
    const handleSelectSubjectVariable = (label)=>{
        insertIntoSubject(label);
        setSubjectPickerAnchor(null);
    };
    const handleSelectMessageVariable = (label)=>{
        insertIntoMessage(label);
        setMessagePickerAnchor(null);
    };
    const openUnsavedChangesModal = useOpenUnsavedChangesModal();
    const { update, isLoading: isSaving, error: saveError } = useUpdateNotificationSettingsV2({
        onSuccess: ()=>onClose()
    });
    const resetEditorFromNotification = (fresh)=>{
        var _fresh_variants, _ref, _ref1;
        const freshVariants = (_fresh_variants = fresh.variants) !== null && _fresh_variants !== void 0 ? _fresh_variants : EMPTY_VARIANTS;
        const freshKeys = deriveVariantKeys(freshVariants);
        const freshRecurring = fresh.type === NotificationSettingsEntryType.Recurring ? fresh.recurring : null;
        const freshMinutes = (_ref = freshRecurring === null || freshRecurring === void 0 ? void 0 : freshRecurring.minutes) !== null && _ref !== void 0 ? _ref : null;
        const freshTimes = (_ref1 = freshRecurring === null || freshRecurring === void 0 ? void 0 : freshRecurring.times) !== null && _ref1 !== void 0 ? _ref1 : null;
        setVariantKeys(freshKeys);
        setVariants(seedVariants(freshVariants, freshKeys));
        if (!freshKeys.includes(activeVariantKey)) setActiveVariantKey(freshKeys[0]);
        setRecipientTypes(fresh.recipientTypes);
        setMinutes(freshMinutes);
        setTimes(freshTimes);
        setHasVendorOverrides(fresh.hasVendorOverrides);
        setBaseline({
            variants: freshVariants,
            recipientTypes: fresh.recipientTypes,
            minutes: freshMinutes,
            times: freshTimes
        });
        setErrors({});
    };
    const handleRevertSuccess = async ()=>{
        if (onRevertSuccess == null) return;
        setIsRefreshingFromRevert(true);
        try {
            const fresh = await onRevertSuccess();
            // The override was cleared on the server but we have no fresh state to populate the
            // editor with. Bail out to the parent so it can re-fetch, rather than leaving the
            // user looking at stale overrides with the Revert button still visible.
            if (fresh == null) onClose();
            else resetEditorFromNotification(fresh);
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    source: 'notification-edit-modal-revert-refresh'
                }
            });
            onClose();
        } finally{
            setIsRefreshingFromRevert(false);
        }
    };
    const { revert, isLoading: isReverting } = useRevertNotificationSetting({
        onSuccess: ()=>{
            void handleRevertSuccess();
        }
    });
    const isDirty = !variantsEqualForKeys(variantKeys, variants, baseline.variants) || !recipientTypesEqual(recipientTypes, baseline.recipientTypes) || minutes !== baseline.minutes || times !== baseline.times;
    const handleSave = ()=>{
        let firstInvalidVariant;
        let firstErrors = {};
        for (const key of variantKeys){
            var _variants_key;
            const variant = (_variants_key = variants[key]) !== null && _variants_key !== void 0 ? _variants_key : EMPTY_VARIANT;
            const variantErrors = validateDraft(variant.subject, variant.message);
            if (!isDraftValid(variantErrors) && firstInvalidVariant == null) {
                firstInvalidVariant = key;
                firstErrors = variantErrors;
            }
        }
        if (firstInvalidVariant != null) {
            setActiveVariantKey(firstInvalidVariant);
            setErrors(firstErrors);
            return;
        }
        setErrors({});
        if (businessId == null) return;
        const variantPayload = {};
        for (const key of variantKeys){
            var _variants_key1;
            const variant = (_variants_key1 = variants[key]) !== null && _variants_key1 !== void 0 ? _variants_key1 : EMPTY_VARIANT;
            variantPayload[key] = {
                subject: variant.subject.trim(),
                message: variant.message.trim()
            };
        }
        // Modal saves never touch the toggle state — that lives behind a separate endpoint —
        // so editing recipients (or content) here cannot accidentally flip an off toggle on.
        const baseUpdate = notification.type === NotificationSettingsEntryType.Recurring ? {
            type: NotificationSettingsEntryType.Recurring,
            recipientTypes,
            recurring: {
                minutes: minutes,
                times: times
            }
        } : {
            type: NotificationSettingsEntryType.Single,
            recipientTypes
        };
        update({
            businessId,
            notificationName,
            data: _object_spread_props(_object_spread({}, baseUpdate), {
                variants: variantPayload
            })
        });
    };
    const handleRevert = async ()=>{
        if (!canRevert) return;
        if (businessId == null) return;
        const confirmed = await openUnsavedChangesModal({
            title: contentTexts.revertConfirmTitle(),
            content: contentTexts.revertConfirmMessage(),
            primaryButtonLabel: contentTexts.revertConfirmConfirm(),
            secondaryButtonLabel: contentTexts.revertConfirmCancel()
        });
        if (!confirmed) return;
        revert({
            businessId,
            notificationName
        });
    };
    const handleCancel = async ()=>{
        if (isDirty) {
            const confirmed = await openUnsavedChangesModal({
                title: contentTexts.discardChangesTitle(),
                content: contentTexts.discardChangesMessage(),
                primaryButtonLabel: contentTexts.discardChangesConfirm(),
                secondaryButtonLabel: contentTexts.discardChangesKeep()
            });
            if (!confirmed) return;
        }
        onClose();
    };
    // Recipients survive toggle-off after ANCR-908, so non-empty recipients no longer imply Active.
    // Active requires both that the toggle is on and that there is someone to notify.
    const isActive = notification.editability.recipients === false || deriveIsActive(notification) && recipientTypes.length > 0;
    const canRevert = hasVendorOverrides;
    const contentEditable = notification.editability.content;
    const saveErrorCode = saveError === null || saveError === void 0 ? void 0 : saveError.code;
    const inlineErrorKey = (_ref2 = (_errors_subject = errors.subject) !== null && _errors_subject !== void 0 ? _errors_subject : errors.message) !== null && _ref2 !== void 0 ? _ref2 : saveErrorCode;
    return /*#__PURE__*/ _jsxs(FullPageModal, {
        contentSkin: "light",
        contentClassName: styles.modalContent,
        headerClassName: styles.modalHeader,
        testId: testId,
        onClose: handleCancel,
        children: [
            /*#__PURE__*/ _jsx(FullPageModal.HeaderStart, {
                children: /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "center",
                    style: {
                        minWidth: 0
                    },
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "h6",
                            weight: "normal",
                            testId: testIds.Title(),
                            children: "".concat(contentTexts.modalTitle(), " - ").concat(notificationLabel)
                        }),
                        /*#__PURE__*/ _jsx(Box, {
                            as: "span",
                            inline: true,
                            className: cn(styles.statusBadge, isActive ? styles.active : styles.inactive),
                            testId: testIds.StatusBadge(),
                            children: isActive ? pageTexts.statusActive() : pageTexts.statusInactive()
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(FullPageModal.MainActions, {
                children: [
                    /*#__PURE__*/ _jsx(TextButton, {
                        size: "small",
                        skin: "dark",
                        underline: "none",
                        loading: isReverting || isRefreshingFromRevert,
                        disabled: !canRevert,
                        tooltip: !canRevert ? {
                            title: contentTexts.revertButtonDisabledTooltip()
                        } : undefined,
                        onClick: handleRevert,
                        prefixIcon: /*#__PURE__*/ _jsx(Undo, {
                            size: 20
                        }),
                        bi: {
                            name: 'revert_to_default'
                        },
                        testId: testIds.RevertButton(),
                        className: styles.revertButton,
                        children: contentTexts.revertButton()
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primaryOutline",
                        size: "small",
                        className: styles.headerCta,
                        onClick: handleCancel,
                        bi: {
                            name: 'cancel'
                        },
                        testId: testIds.CancelButton(),
                        children: contentTexts.cancelButton()
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primary",
                        size: "small",
                        loading: isSaving,
                        className: styles.headerCta,
                        onClick: handleSave,
                        bi: {
                            name: 'save'
                        },
                        testId: testIds.SaveButton(),
                        children: contentTexts.saveButton()
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(BiContainer, {
                name: "edit_notification",
                children: [
                    variantKeys.length > 1 && /*#__PURE__*/ _jsx("div", {
                        className: styles.stickyTabs,
                        children: /*#__PURE__*/ _jsx(Tabs, {
                            bi: {
                                name: 'notification_edit_variant_tabs'
                            },
                            value: activeVariantKey,
                            onChange: (_event, nextValue)=>{
                                setActiveVariantKey(nextValue);
                                setErrors({});
                            },
                            testId: testIds.VariantTabs(),
                            tabs: variantKeys.map((key)=>({
                                    id: key,
                                    value: key,
                                    component: resolveVariantLabel(notificationName, key, contentTexts.variantLabels),
                                    testId: testIds.VariantTab(key)
                                }))
                        })
                    }),
                    /*#__PURE__*/ _jsx(Column, {
                        gap: 24,
                        padding: 32,
                        testId: testIds.Root(),
                        className: styles.card,
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: styles.bodyGrid,
                            children: [
                                /*#__PURE__*/ _jsxs(Column, {
                                    gap: 16,
                                    children: [
                                        /*#__PURE__*/ _jsxs(Column, {
                                            gap: 4,
                                            children: [
                                                /*#__PURE__*/ _jsx(TextField, {
                                                    label: contentTexts.subjectLabel(),
                                                    value: subject,
                                                    onChange: (event)=>setSubject(event.target.value),
                                                    placeholder: contentTexts.subjectPlaceholder(),
                                                    error: errors.subject != null,
                                                    disabled: !contentEditable,
                                                    bi: {
                                                        name: 'subject'
                                                    },
                                                    testId: testIds.SubjectField(),
                                                    inputRef: subjectInputRef,
                                                    InputProps: contentEditable ? {
                                                        endAdornment: /*#__PURE__*/ _jsx("span", {
                                                            ref: subjectAdornmentRef,
                                                            children: /*#__PURE__*/ _jsx(VariableFieldAdornment, {
                                                                testId: testIds.SubjectVariableAdornment(),
                                                                onClick: ()=>setSubjectPickerAnchor(subjectAdornmentRef.current)
                                                            })
                                                        })
                                                    } : undefined
                                                }),
                                                errors.subject != null && /*#__PURE__*/ _jsx(Typography, {
                                                    skin: "error",
                                                    variant: "paragraph3",
                                                    testId: testIds.SubjectError(),
                                                    children: resolveErrorText(contentTexts.errors, errors.subject)
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(Column, {
                                            gap: 4,
                                            children: [
                                                /*#__PURE__*/ _jsx(Typography, {
                                                    variant: "paragraph3",
                                                    weight: "light",
                                                    skin: "secondary",
                                                    children: contentTexts.messageLabel()
                                                }),
                                                /*#__PURE__*/ _jsx(TextField, {
                                                    multiline: true,
                                                    minRows: 6,
                                                    value: message,
                                                    onChange: (event)=>setMessage(event.target.value),
                                                    placeholder: contentTexts.messagePlaceholder(),
                                                    error: errors.message != null,
                                                    disabled: !contentEditable,
                                                    bi: {
                                                        name: 'message'
                                                    },
                                                    testId: testIds.MessageField(),
                                                    inputRef: messageInputRef,
                                                    InputProps: contentEditable ? {
                                                        endAdornment: /*#__PURE__*/ _jsx("span", {
                                                            ref: messageAdornmentRef,
                                                            className: styles.messageAdornment,
                                                            children: /*#__PURE__*/ _jsx(VariableFieldAdornment, {
                                                                testId: testIds.MessageVariableAdornment(),
                                                                onClick: ()=>setMessagePickerAnchor(messageAdornmentRef.current)
                                                            })
                                                        })
                                                    } : undefined
                                                }),
                                                errors.message != null && /*#__PURE__*/ _jsx(Typography, {
                                                    skin: "error",
                                                    variant: "paragraph3",
                                                    testId: testIds.MessageError(),
                                                    children: resolveErrorText(contentTexts.errors, errors.message)
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(VariablePickerPopover, {
                                            anchorEl: subjectPickerAnchor,
                                            customVariables: pickerCustomVariables,
                                            allowedSystemKeys: pickerAllowedSystemKeys,
                                            onSelect: handleSelectSubjectVariable,
                                            onClose: ()=>setSubjectPickerAnchor(null),
                                            testId: testIds.SubjectVariablePicker()
                                        }),
                                        /*#__PURE__*/ _jsx(VariablePickerPopover, {
                                            anchorEl: messagePickerAnchor,
                                            customVariables: pickerCustomVariables,
                                            allowedSystemKeys: pickerAllowedSystemKeys,
                                            onSelect: handleSelectMessageVariable,
                                            onClose: ()=>setMessagePickerAnchor(null),
                                            testId: testIds.MessageVariablePicker()
                                        }),
                                        inlineErrorKey != null && inlineErrorKey !== errors.subject && inlineErrorKey !== errors.message && /*#__PURE__*/ _jsx(Typography, {
                                            skin: "error",
                                            variant: "paragraph3",
                                            testId: testIds.InlineError(),
                                            children: (_resolveErrorText = resolveErrorText(contentTexts.errors, inlineErrorKey)) !== null && _resolveErrorText !== void 0 ? _resolveErrorText : contentTexts.errors.default()
                                        }),
                                        /*#__PURE__*/ _jsx(NotifyRow, {
                                            selectedTab: selectedTab,
                                            notificationType: notification.type,
                                            notificationName: notificationName,
                                            recipientTypes: recipientTypes,
                                            recipientTypeOptions: notification.recipientTypeOptions,
                                            recipientsEditable: notification.editability.recipients,
                                            minutes: minutes,
                                            times: times,
                                            onChangeRecipients: setRecipientTypes,
                                            onChangeMinutes: setMinutes,
                                            onChangeTimes: setTimes
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Column, {
                                    gap: 12,
                                    testId: testIds.PreviewRoot(),
                                    children: /*#__PURE__*/ _jsx(NotificationEmailPreview, {
                                        notificationName: notificationName,
                                        subject: subject,
                                        message: message,
                                        templateVariables: (_notification_templateVariables = notification.templateVariables) !== null && _notification_templateVariables !== void 0 ? _notification_templateVariables : {},
                                        customTemplateVariables: customTemplateVariables,
                                        ctaButtonText: notification.ctaButtonText,
                                        branding: branding
                                    })
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
};
